/**
 * Console Redactor
 *
 * Intercepts console methods to redact sensitive infrastructure details
 * like Supabase project IDs from browser console logs.
 *
 * This module MUST be initialized BEFORE any other code runs to ensure
 * all console output is redacted, including errors from third-party libraries.
 *
 * Security: Prevents exposure of infrastructure identifiers in browser DevTools.
 */

// Extract Supabase project ID from environment URL
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const projectIdMatch = supabaseUrl.match(/https?:\/\/([^.]+)\.supabase\.co/);
const PROJECT_ID = projectIdMatch ? projectIdMatch[1] : null;

// Patterns to redact (add more as needed)
const REDACTION_PATTERNS = PROJECT_ID ? [
  // Match the full subdomain pattern: projectid.supabase.co
  {
    pattern: new RegExp(`${PROJECT_ID}\\.supabase\\.co`, 'gi'),
    replacement: '[supabase-redacted].supabase.co'
  },
  // Match just the project ID in URLs or strings
  {
    pattern: new RegExp(`https?://${PROJECT_ID}\\.`, 'gi'),
    replacement: 'https://[supabase-redacted].'
  }
] : [];

/**
 * Redact sensitive patterns from a value
 * Handles strings, objects, arrays, and Error objects
 */
function redactValue(value) {
  if (!PROJECT_ID || REDACTION_PATTERNS.length === 0) {
    return value;
  }

  // Handle null/undefined
  if (value == null) {
    return value;
  }

  // Handle strings
  if (typeof value === 'string') {
    let result = value;
    for (const { pattern, replacement } of REDACTION_PATTERNS) {
      result = result.replace(pattern, replacement);
    }
    return result;
  }

  // Handle Error objects
  if (value instanceof Error) {
    const redactedError = new Error(redactValue(value.message));
    redactedError.name = value.name;
    if (value.stack) {
      redactedError.stack = redactValue(value.stack);
    }
    return redactedError;
  }

  // Handle arrays
  if (Array.isArray(value)) {
    return value.map(item => redactValue(item));
  }

  // Handle plain objects (but not DOM nodes, Promises, etc.)
  if (typeof value === 'object' && value.constructor === Object) {
    const redacted = {};
    for (const [key, val] of Object.entries(value)) {
      redacted[key] = redactValue(val);
    }
    return redacted;
  }

  // Return other types unchanged (numbers, booleans, functions, DOM elements, etc.)
  return value;
}

/**
 * Redact all arguments passed to console methods
 */
function redactArgs(args) {
  return args.map(arg => redactValue(arg));
}

// Store original console methods
const originalConsole = {
  log: console.log.bind(console),
  warn: console.warn.bind(console),
  error: console.error.bind(console),
  info: console.info.bind(console),
  debug: console.debug.bind(console),
  trace: console.trace.bind(console),
};

// Track if already initialized
let isInitialized = false;

/**
 * Initialize console redaction
 *
 * Call this BEFORE any other initialization code in main.jsx
 * to ensure all console output is redacted.
 *
 * @example
 * // In main.jsx, add as first import and call:
 * import { initConsoleRedactor } from './lib/consoleRedactor';
 * initConsoleRedactor();
 */
export function initConsoleRedactor() {
  if (isInitialized) {
    return;
  }

  if (!PROJECT_ID) {
    // No Supabase URL configured, skip redaction
    originalConsole.debug('[ConsoleRedactor] No Supabase URL found, skipping initialization');
    return;
  }

  // Override console methods with redacting versions
  console.log = (...args) => originalConsole.log(...redactArgs(args));
  console.warn = (...args) => originalConsole.warn(...redactArgs(args));
  console.error = (...args) => originalConsole.error(...redactArgs(args));
  console.info = (...args) => originalConsole.info(...redactArgs(args));
  console.debug = (...args) => originalConsole.debug(...redactArgs(args));
  console.trace = (...args) => originalConsole.trace(...redactArgs(args));

  isInitialized = true;

  // Log initialization (using original to avoid recursion issues during init)
  if (import.meta.env.VITE_ENVIRONMENT === 'development') {
    originalConsole.debug('[ConsoleRedactor] Initialized - Supabase project ID will be redacted from logs');
  }
}

/**
 * Get original console for internal logging
 * Use this when you need to bypass redaction (e.g., for debugging the redactor itself)
 */
export function getOriginalConsole() {
  return originalConsole;
}

/**
 * Check if console redaction is active
 */
export function isRedactionActive() {
  return isInitialized && PROJECT_ID !== null;
}
